import { v4 } from "uuid";
import { API_KEY } from "../../constant/environment";
import { VOICE_MODE } from "../../constant/voiceMode";
import { decryptData, encryptData } from "../../utils/api/encryption";
import { getChatId } from "../../utils/chat/chatId";
import { getSessionId } from "../../utils/sessionId";
import { XENIA_CHAT_URL } from "../../utils/sse/sseTransport";
import { Fragment, HandledBy } from "../../services/voice/liveTranscript";
import { CleanRequest, TranscriptCleaner, readTranscriptFrame } from "../../services/voice/transcriptCleaner";
import { DelegationRecord, TranscriptLine } from "../../services/voice/transcriptBackup";
import type { HandOff, VoiceSession } from "./types";
import { VoiceRuntime, voiceLog, now } from "./runtime";

const TRANSCRIPT_CLEAN = "transcriptClean";

export const readStatus = (body: string): boolean => {
  try {
    const decrypted = decryptData(body);
    const parsed = typeof decrypted === "string" ? JSON.parse(decrypted) : decrypted;
    return parsed?.statusCode === 200;
  } catch {
    return false;
  }
};

export const createTranscriptIO = (rt: VoiceRuntime) => {
  const { live, chatIdRef, backupRef, handlers } = rt;

  const writeLine = (session: VoiceSession, line: TranscriptLine) => {
    if (session.id) backupRef.current?.write(session.id, chatIdRef.current, "line", line);
  };

  const writeUserLines = (
    session: VoiceSession,
    fragments: Fragment[],
    handledBy: HandledBy,
    delegationId: string | null,
    traceId: string | null,
  ) => {
    fragments.forEach((fragment) => {
      if (session.written.has(fragment)) return;
      session.written.add(fragment);
      writeLine(session, {
        speaker: "user",
        mode: "voice",
        handledBy,
        text: fragment.text,
        startMs: fragment.startMs,
        endMs: fragment.endMs,
        delegationId,
        traceId,
      });
    });
  };

  const writeDelegation = (liveSessionId: string | null, record: DelegationRecord) => {
    if (liveSessionId) backupRef.current?.write(liveSessionId, chatIdRef.current, "delegation", { ...record });
  };

  const adoptChatId = (liveSessionId: string | null) => {
    chatIdRef.current = getChatId();
    if (liveSessionId && chatIdRef.current) backupRef.current?.adoptChat(liveSessionId, chatIdRef.current);
  };

  const writeJoined = (session: VoiceSession, pending: HandOff, traceId: string | null, outcome: "sent" | "busy") => {
    pending.joined?.forEach((extra) =>
      writeDelegation(session.id, { id: extra.id, offsetMs: extra.offsetMs, requestText: "", traceId, outcome, stoppedByUser: false }),
    );
  };

  const sendClean = async (session: VoiceSession, request: CleanRequest): Promise<string> => {
    const at = now();
    const traceId = v4().slice(0, 8);
    const response = await fetch(XENIA_CHAT_URL, {
      method: "POST",
      headers: {
        "Content-Type": "text/plain",
        "x-access-token": live.current.state.accessJWT,
        "x-api-key": API_KEY || "",
        "x-trace-id": traceId,
      },
      body: encryptData({
        requestType: TRANSCRIPT_CLEAN,
        ...request,
        liveSessionId: session.id,
        chatId: chatIdRef.current,
        sessionId: getSessionId(),
        traceId,
      }),
    });
    const text = await readTranscriptFrame(response, request.seq);
    if (text === null) throw new Error(`no transcript frame, status ${response.status}`);
    voiceLog("clean", { seq: request.seq, speaker: request.speaker, ms: now() - at, chars: request.text.length, cleanChars: text.length });
    return text;
  };

  const cleanerOf = (session: VoiceSession): TranscriptCleaner => {
    if (!session.cleaner) {
      session.cleaner = new TranscriptCleaner(
        (request) => sendClean(session, request),
        () => handlers.current.onCleaned(session),
        (step, payload) => {
          if (step === "clean.failed") session.stats.cleanFailed += 1;
          voiceLog(step, payload);
        },
        VOICE_MODE.cleanCadenceMs,
        VOICE_MODE.cleanWaitMs,
      );
    }
    return session.cleaner;
  };

  return { writeLine, writeUserLines, writeDelegation, adoptChatId, writeJoined, sendClean, cleanerOf };
};

export type TranscriptIO = ReturnType<typeof createTranscriptIO>;
